import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    url: String,
    placeholder: String,
    allowClear: Boolean,
    closeOnSelect: Boolean,
    delay: Number,
    submitOnChange: Boolean,
    containerSelector: String
  }

  static classes = [
    "resultsContainer",
    "resultsOption", 
    "hierarchyHidden",
    "managerOption",
    "podLeaderOption",
    "podEmployeeOption"
  ]

  connect() {
    this.initializeSelect2()
    this.setupEventListeners()
    this.resetHiddenInputs()
    this.initializeHiddenFields()
  }

  resetHiddenInputs() {
    this.hiddenInputs = { team_manager_ids: [], pod_leader_ids: [], pod_employee_ids: [] }
  }

  disconnect() {
    const $element = $(this.element)
    if ($element.data('select2')) {
      $element.select2('destroy')
    }
  }

  initializeSelect2() {
    const $element = $(this.element)
    
    this.select2Instance = $element.select2({
      placeholder: this.placeholderValue,
      allowClear: this.hasAllowClearValue ? this.allowClearValue : true,
      closeOnSelect: this.hasCloseOnSelectValue ? this.closeOnSelectValue : false,
      ajax: {
        url: this.urlValue,
        dataType: 'json',
        delay: this.hasDelayValue ? this.delayValue : 250,
        data: (params) => ({
          q: params.term,
          page: params.page || 1
        }),
        processResults: (data) => this.processHierarchyResults(data),
        cache: true
      },
      templateResult: (option) => this.formatOption(option),
      templateSelection: (option) => this.formatSelection(option)
    })

    // Remove the name from select so it doesn't submit
    $element.attr('name', '')
  }

  setupEventListeners() {
    const $element = $(this.element)

    $element.on('change', () => {
      this.updateHiddenFields()
    });

    // Only submit/dispatch on dropdown close. This allows the user to
    // select multiple options w/o the page reloading on each selection.
    $element.on('select2:close', () => {
      this.dispatchTeamMemberSelectChangedUpdate()

      if (this.hasSubmitOnChangeValue ? this.submitOnChangeValue : true) {
        this.submitForm()
      }
    })

    // Clicking the allowClear "x" does not open/close the dropdown, so
    // select2:close never fires. Treat clear like close so filters update.
    // Note: select2:clear fires *before* the `change` that would reset
    // hiddenInputs — reset explicitly so the dispatched payload is empty.
    $element.on('select2:clear', () => {
      this.resetHiddenInputs()
      this.dispatchTeamMemberSelectChangedUpdate()

      if (this.hasSubmitOnChangeValue ? this.submitOnChangeValue : true) {
        this.submitForm()
      }
    })
  }

  dispatchTeamMemberSelectChangedUpdate() {
    const event = new CustomEvent('bk360-team-member-select-changed', { 
      bubbles: true,
      detail: this.hiddenInputs
    });
    document.dispatchEvent(event);
  }

    processHierarchyResults(data) {
      const results = []
      
      data.forEach((department) => {
        const departmentGroup = {
          text: department.department_name,
          children: []
        }
        
        department.teams.forEach((team) => {
          // Add team manager directly to department
          if(team.manager_name) { // NOTE: May be skipped if current user lacks "All" scope on permission
            departmentGroup.children.push({
              id: team.manager_id,
              text: team.manager_name,
              role: 'Team Manager',
              level: 'manager',
              data: { role: 'manager' }
            })
          }
          
          // Add pod leaders "under" (after) this manager
          team.pod_leaders.forEach((leader) => {
            departmentGroup.children.push({
              id: leader.id,
              text: leader.name,
              role: 'Pod Leader',
              level: 'pod_leader',
              data: { role: 'pod_leader' }
            })
            
            // Add pod employees under this pod leader (if they exist)
            if (leader.pod_employees && leader.pod_employees.length > 0) {
              leader.pod_employees.forEach((employee) => {
                departmentGroup.children.push({
                  id: employee.id,
                  text: employee.name,
                  role: 'Pod Employee',
                  level: 'pod_employee',
                  data: { role: 'pod_employee' }
                })
              })
            }
          })
        })
        
        results.push(departmentGroup)
      })
      
      return { results }
    }

  formatOption(option) {
    if (!option.id) return option.text
    if (option.children) return $('<span class="fw-bold">').text(option.text)
    
    const $result = $('<div class="d-flex align-items-center">')
    const $name = $('<span class="flex-grow-1 text-truncate pe-2">').text(option.text)
    const $role = $('<small class="text-muted flex-shrink-0">').text(option.role)
    
    // Visual styling based on role
    if (option.level === 'manager') {
      $result.addClass('border-bottom border-light')
      $name.addClass('fw-semibold')
      if (this.hasManagerOptionClass) {
        $result.addClass(this.managerOptionClass)
      }
    } else if (option.level === 'pod_leader') {
      $name.addClass('ms-4')
      if (this.hasPodLeaderOptionClass) {
        $result.addClass(this.podLeaderOptionClass)
      }
    } else if (option.level === 'pod_employee') {
      $name.addClass('ms-5') // Further indented than pod leaders
      if (this.hasPodEmployeeOptionClass) {
        $result.addClass(this.podEmployeeOptionClass)
      }
    }

    $result.append($name)
    $result.append($role)
    
    return $result
  }

  formatSelection(option) {
    if (!option.role) return option.text
    return option.text + ' (' + option.role + ')'
  }

  updateHiddenFields() {
    this.resetHiddenInputs();

    let container
    if (this.hasContainerSelectorValue) {
      container = $(this.containerSelectorValue)
    } else {
      // Fallback to closest form for backward compatibility
      container = $(this.element).closest('form')
    }
    
    if (!container.length) {
      console.warn('No container found for hidden fields')
      return
    }

    const hiddenClass = this.hasHierarchyHiddenClass ? this.hierarchyHiddenClass : 'hierarchy-hidden'
    
    container.find(`.${hiddenClass}`).remove()

    const selectedData = $(this.element).select2('data') || []

    selectedData.forEach((option) => {
      let role = option.data?.role

      // Fallback: determine role from option text if data.role not available
      if (!role && option.text) {
        if (option.text.includes('Team Manager')) {
          role = 'manager'
        } else if (option.text.includes('Pod Leader')) {
          role = 'pod_leader'
        } else if (option.text.includes('Pod Employee')) {
          role = 'pod_employee'
        }
      }

      // Additional fallback: check the original Rails data
      if (!role && option.id) {
        const optionElement = $(this.element).find('option[value="' + option.id + '"]')
        if (optionElement.length) {
          const optionText = optionElement.text()
          if (optionText.includes('Team Manager')) {
            role = 'manager'
          } else if (optionText.includes('Pod Leader')) {
            role = 'pod_leader'
          } else if (optionText.includes('Pod Employee')) {
            role = 'pod_employee'
          }
        }
      }

      if (role) {
        let fieldName
        if (role === 'manager') {
          fieldName = 'team_manager_ids[]'
          this.hiddenInputs.team_manager_ids.push(option.id)
        } else if (role === 'pod_leader') {
          fieldName = 'pod_leader_ids[]'
          this.hiddenInputs.pod_leader_ids.push(option.id)
        } else if (role === 'pod_employee') {
          fieldName = 'pod_employee_ids[]'
          // Initialize pod_employee_ids array if it doesn't exist
          if (!this.hiddenInputs.pod_employee_ids) {
            this.hiddenInputs.pod_employee_ids = []
          }
          this.hiddenInputs.pod_employee_ids.push(option.id)
        }
        
        if (fieldName) {
          $(`<input type="hidden" class="${hiddenClass}">`)
            .attr('name', fieldName)
            .val(option.id)
            .appendTo(container)
        }
      }
    })
  }

  initializeHiddenFields() {
    this.updateHiddenFields()
  }

  submitForm() {
    if (this.hasContainerSelectorValue) {
      const container = $(this.containerSelectorValue)
      const form = container.is('form') ? container[0] : container.closest('form')[0]
      if (form) {
        form.submit()
      }
    } else {
      // Fallback behavior
      const form = $(this.element).closest('form')[0]
      if (form) {
        form.submit()
      }
    }
  }
}
